import { Box, CircularProgress } from "@mui/material";
import axios from "axios";
import { marked } from "marked";
import { useEffect, useState } from "react";

import MarkdownRenderer from "../copilot/markdown-view";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../ui/tabs";

import { getFileTypeFromGCSPath } from "@/utils/util";
import "./pdf-viewer-iframe.css";

const PDFViewer = ({ gcsPath }: any) => {
  const [pdfUrl, setPdfUrl] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const [downloadProgress, setDownloadProgress] = useState(0);
  const [fileType, setFileType] = useState(getFileTypeFromGCSPath(gcsPath));
  const [content, setContent] = useState("");

  useEffect(() => {
    const fileTypeValue = getFileTypeFromGCSPath(gcsPath);
    setFileType(fileTypeValue);
    if (isLoading) return;

    const fetchFile = async () => {
      setIsLoading(true);
      const accessToken = localStorage.getItem("accessToken");
      const data = {
        gcs_path: gcsPath || "",
      };
      try {
        const response = await axios.post(
          `${import.meta.env.VITE_API_URL}/download-gcs-file-chunked`,
          data,
          {
            headers: {
              Authorization: `Bearer ${accessToken}`,
              "Content-Type": "application/json",
            },
            onDownloadProgress: (progressEvent: any) => {
              if (progressEvent.total) {
                const progress = Math.round(
                  (progressEvent.loaded / progressEvent.total) * 100
                );
                setDownloadProgress(progress);
              } else {
                setDownloadProgress(-1);
              }
            },
            responseType: "blob",
          }
        );

        if (response.status === 200) {
          const blob = response.data;

          if (fileType === "txt") {
            const text = await blob.text();
            setContent(text);
          } else {
            const url = window.URL.createObjectURL(blob);
            setPdfUrl(url as any);
          }
          setDownloadProgress(0);
        }
      } catch (error) {
        console.error("Error fetching file:", error);
      } finally {
        setIsLoading(false);
      }
    };

    fetchFile();
  }, [gcsPath]);

  return (
    <div className="flex justify-center items-center">
      {isLoading && (
        <div className="my-4">
          <div className="font-semibold">Loading...</div>
          <Box sx={{ width: "100%", my: 2, borderRadius: 3 }}>
            {downloadProgress === -1 ? (
              <CircularProgress />
            ) : (
              <CircularProgress
                variant="determinate"
                value={downloadProgress}
              />
            )}
          </Box>
        </div>
      )}{" "}
      {fileType === "txt" && !isLoading && (
        <div className="flex">
          <Tabs defaultValue="markdown" className="ml-4">
            <TabsList className="mb-6">
              <TabsTrigger value="markdown" className="font-semibold min-w-20">
                Markdown
              </TabsTrigger>
              <TabsTrigger value="text" className="font-semibold">
                Text
              </TabsTrigger>
            </TabsList>
            <TabsContent value="markdown">
              <MarkdownRenderer
                content={marked(content) as any}
                maxHeight="max-h-[75vh]"
                prose={true}
              />
            </TabsContent>
            <TabsContent value="text">
              <div className="overflow-scroll max-h-[75vh]">{content}</div>
            </TabsContent>
          </Tabs>
        </div>
      )}
      {pdfUrl && fileType !== "txt" && !isLoading && <iframe src={pdfUrl} />}
    </div>
  );
};

export default PDFViewer;
